import React from 'react';
import Header from '../Header';
import { Box } from '@mui/material';
import BuildingDevicesCoreTable from '../tables/BuildingDevicesCoreTable';
import BuildingDevicesDistroTable from '../tables/BuildingDevicesDistroTable';
import BuildingDevicesIspTable from '../tables/BuildingDevicesIspTable';
import BuildingDevicesAccessSwitchTable from '../tables/BuildingDevicesAccessSwitchTable';
import BuildingDevicesHostTable from '../tables/BuildingDevicesHostTable';
import Typography from '@mui/material/Typography';
import BrokenLinkPage from './BrokenLinkPage';
import { useParams } from 'react-router-dom';
import {
  BuildingDevicesHostDataType,
  BuildingDevicesStructureDataType,
  BuildingNameDataType,
} from '../../types/buildingTypes';
import { ApiInstance } from '../../api/api';
import { DevicesHostsDataContext } from '../../context/devicesHostsDataContext';
import { getHostStatus } from '../../utils/hostFormatter';
import { useRequiredSearchParams } from '../../hooks/useRequiredSearchParams';
import DynamicBreadcrumbs from '../breadcrumbs/DynamicBreadcrumbs';
import pageStyles from './pageStyles';

function DevicesPage() {
  const { building } = useParams<BuildingNameDataType>();
  const { params, isValid } = useRequiredSearchParams(['region']);
  const [isLoading, setIsLoading] = React.useState(false);
  const [allHosts, setAllHosts] = React.useState<BuildingDevicesHostDataType[]>([]);
  const [hosts, setHosts] = React.useState<BuildingDevicesHostDataType[]>([]);
  const [cores, setCores] = React.useState<BuildingDevicesStructureDataType[]>([]);
  const [distros, setDistros] = React.useState<BuildingDevicesStructureDataType[]>([]);
  const [ispDevices, setIspDevices] = React.useState<BuildingDevicesStructureDataType[]>([]);
  const [accessSwitches, setAccessSwitches] = React.useState<BuildingDevicesStructureDataType[]>(
    []
  );
  const [loadingState, setLoadingState] = React.useState<Boolean>(false);
  const [apiErrorFlag, setApiErrorFlag] = React.useState(false);

  const getBuildingDevices = async () => {
    try {
      setIsLoading(true);

      const buildingDevices = await ApiInstance.getBuildingDevices(building, params.region);

      const processedHosts: BuildingDevicesHostDataType[] = buildingDevices.host.map(
        (host: BuildingDevicesHostDataType) => {
          const { active, formattedDate } = getHostStatus(host.last_seen);

          return {
            ...host,
            last_seen: formattedDate,
            active,
          };
        }
      );

      setAllHosts(processedHosts);
      setHosts(processedHosts);
      setCores(buildingDevices.core);
      setDistros(buildingDevices.distro);
      setIspDevices(buildingDevices.isp_devices);
      setAccessSwitches(buildingDevices.access_switch);

      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      setApiErrorFlag(true);
    }
  };

  React.useEffect(() => {
    if (!isValid) return;
    getBuildingDevices();
  }, [isValid]);

  if (!isValid) return <BrokenLinkPage />;

  return (
    <>
      <Header />
      {
        <Box sx={{ margin: 'auto', width: '90%' }}>
          <Box sx={pageStyles.dynamicBreadcrumbs}>
            <DynamicBreadcrumbs />
          </Box>
          <Typography sx={pageStyles.label} component="h1" variant="h4" align="center">
            Building manager - region: {params.region}, building: {building}
          </Typography>
          <BuildingDevicesCoreTable
            building={building}
            selectedRegion={params.region}
            cores={cores}
            isLoading={isLoading}
            apiErrorFlag={apiErrorFlag}
          />
          <BuildingDevicesDistroTable
            building={building}
            selectedRegion={params.region}
            distros={distros}
            isLoading={isLoading}
            apiErrorFlag={apiErrorFlag}
          />
          <BuildingDevicesIspTable
            building={building}
            selectedRegion={params.region}
            ispDevices={ispDevices}
            isLoading={isLoading}
            apiErrorFlag={apiErrorFlag}
          />
          <BuildingDevicesAccessSwitchTable
            building={building}
            selectedRegion={params.region}
            accessSwitches={accessSwitches}
            isLoading={isLoading}
            apiErrorFlag={apiErrorFlag}
          />
          <DevicesHostsDataContext.Provider
            value={{ allHosts, hosts, loadingState, setHosts, setLoadingState }}
          >
            <BuildingDevicesHostTable
              building={building}
              hosts={hosts}
              allHosts={allHosts}
              cores={cores}
              distros={distros}
              accessSwitches={accessSwitches}
              selectedRegion={params.region}
              isLoading={isLoading}
              apiErrorFlag={apiErrorFlag}
            />
          </DevicesHostsDataContext.Provider>
        </Box>
      }
    </>
  );
}

export default DevicesPage;
